import React, { Component } from "react";
import { Row, Col } from "react-flexbox-grid";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle
} from "material-ui-next/Dialog";
import Typography from "material-ui-next/Typography";
import Icon from "material-ui-next/Icon";
import Divider from "material-ui-next/Divider";
import Tooltip from "material-ui-next/Tooltip";
import { FormattedNumber } from "react-intl";

import {
  CheckBox,
  DataTable,
  Loadable,
  SelectField,
  StyledButton
} from "components/support";
import { PaymentTypes, AcquirerNames } from "core/enum";
import { fn } from "util";
import * as R from "ramda";
import { colors } from "assets";

import style from "./style";

class MyFinancePendingTransactionsView extends Component {
  friendlyType = payment => {
    const getInstallments = R.pathOr(1, ["metadata", "installments"]);
    if (getInstallments(payment) > 1) {
      return PaymentTypes.installments;
    }

    return PaymentTypes[payment.type];
  };

  findLinkedPayments = payment_uuid => {
    const { receivables } = this.props.viewModel;
    const receivable = receivables.find(r => {
      const paymentsUuids = r.ambiguous_payments.map(ap => ap.payment.uuid);
      return paymentsUuids.includes(payment_uuid);
    });

    if (!receivable) {
      return {};
    }

    const payment = (receivable.linked_payments || []).find(
      lp => lp.uuid === payment_uuid
    );
    return payment || {};
  };

  containsAccount(array, id) {
    for (let i = 0; i < array.length; i++) {
      if (array[i].value === id) return true;
    }
    return false;
  }

  allAccountsSelectField = ambiguous_payments => {
    const allOptions = [];

    for (const ap of ambiguous_payments) {
      for (const af of ap.accounts_found) {
        const accountExists = this.containsAccount(
          allOptions,
          af.sale_account.id
        );
        if (!accountExists) {
          allOptions.push({
            value: af.sale_account.id,
            label: af.sale_account.name
          });
        }
      }
    }

    return (
      <SelectField
        value={true}
        selectfieldstyle={{ margin: 0 }}
        onChange={(_, value) =>
          this.props.presenter.onChangeAll(value, this.getSelectedCheckBoxes())
        }
        underlineStyle={{ bottom: "0" }}
        labelStyle={{ height: "44px" }}
        fullWidth
        options={[{ value: "", label: "Selecionar" }, ...allOptions]}
      />
    );
  };

  getSelectedCheckBoxes = () => {
    const { checkedRows } = this.props.viewModel;
    return Object.entries(checkedRows)
      .filter(([_, checked]) => checked)
      .map(([payment_uuid, _]) => payment_uuid);
  };

  allCheckBoxesSelected = () => {
    const { checkedRows, payments } = this.props.viewModel;
    for (const ap of payments) {
      if (!checkedRows[ap.payment.uuid]) {
        return false;
      }
    }
    return true;
  };

  selectAllCheckBoxes = checked => {
    const { payments } = this.props.viewModel;
    const checkedRows = payments.reduce(
      (acc, ap) => ({ ...acc, [ap.payment.uuid]: checked }),
      {}
    );
    this.props.presenter.onCheckBoxEnqueue(checkedRows);
  };

  clearCheckBoxes = () => {
    this.props.presenter.onCheckBoxEnqueue({});
  };

  accountSelectField = (accounts, payment_uuid) => {
    const options = accounts.map(account => {
      let accountOptions = {};
      accountOptions.value = account.sale_account.id;
      accountOptions.label = account.sale_account.name;
      return accountOptions;
    });

    return (
      <SelectField
        value={this.findLinkedPayments(payment_uuid).selected}
        selectfieldstyle={{ margin: 0 }}
        onChange={(_, value) =>
          this.props.presenter.onChange(value, payment_uuid)
        }
        underlineStyle={{ bottom: "0" }}
        labelStyle={{ height: "44px" }}
        fullWidth
        options={[{ value: "", label: "Selecionar" }, ...options]}
      />
    );
  };

  accountNotFound = () => (
    <div className={this.props.className}>
      <Tooltip
        key="tooltip-top-myfinance"
        title="Crie uma Conta de Recebível no MyFinance para víncular"
        placement="top"
      >
        <Icon className="tooltip-icon">info</Icon>
      </Tooltip>
    </div>
  );

  config = {
    identifier: "uuid",
    columns: [
      {
        id: "checkboxes",
        Header: () => (
          <CheckBox
            checked={this.allCheckBoxesSelected()}
            style={{ padding: 0 }}
            onCheck={checked => this.selectAllCheckBoxes(checked)}
            squareIconStyle={{ backgroundColor: "#FFFFFF", margin: 2 }}
            checkBoxStyle={{ border: "2px solid #FFFFFF" }}
          />
        ),
        Cell: props => (
          <CheckBox
            checked={
              this.props.viewModel.checkedRows[props.original.payment.uuid]
            }
            style={{ padding: 0 }}
            onCheck={checked =>
              this.props.presenter.onSingleCheckBoxEnqueue({
                [props.original.payment.uuid]: checked
              })
            }
            checkBoxStyle={{ border: `1px solid ${colors.primary.normal}` }}
          />
        ),
        align: "center",
        sortable: false
      },
      {
        id: "payment.processed_at",
        accessor: "payment.processed_at",
        Header: "Data",
        type: "datetime",
        align: "center",
        sortable: false
      },
      {
        id: "amount",
        Header: "Total",
        type: "custom",
        align: "right",
        Cell: props => (
          <FormattedNumber
            value={fn.centsToReal(props.original.payment.amount)}
            format="BRL"
          />
        ),
        sortable: false
      },
      {
        id: "type",
        Header: "Pagamento",
        type: "custom",
        align: "center",
        Cell: props => this.friendlyType(props.original.payment),
        sortable: false
      },
      {
        accessor: "payment.metadata.acquirer_name",
        Header: "Credenciadora",
        align: "center",
        type: "enum",
        params: { enum: AcquirerNames },
        sortable: false
      },
      {
        id: "receivables.account",
        Header: "Conta de Recebível",
        align: "center",
        Cell: props =>
          props.original.accounts_found
            ? this.accountSelectField(
                props.original.accounts_found,
                props.original.payment.uuid
              )
            : this.accountNotFound(),
        sortable: false
      }
    ]
  };

  render() {
    const {
      className,
      presenter,
      viewModel,
      pendingTransactionsOpen,
      onPendingTransactionsClose
    } = this.props;
    const { loading, accountName, entityName, pagination } = viewModel;
    const { payments } = viewModel || {};
    const { page, perPage, total } = pagination;

    return (
      <Dialog
        open={pendingTransactionsOpen}
        onClose={() => onPendingTransactionsClose()}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        maxWidth="md"
        fullWidth={true}
      >
        <Loadable loading={loading} className={className}>
          <DialogTitle id="form-dialog-title" className="text-center">
            <Row className="p-0" end="xs">
              <Col xs={12}>
                <div
                  className="close-icon"
                  onClick={() => onPendingTransactionsClose()}
                >
                  <Icon>close</Icon>
                </div>
              </Col>
            </Row>
            <Row center="xs" className="title-row">
              <Col xs={12}>
                <Typography type="headline">
                  Conciliação de Transações Pendentes
                </Typography>
              </Col>
            </Row>
          </DialogTitle>

          <DialogContent className="text-center dialog-content pb-0">
            <Row bottom="xs" className="mb-sm">
              <Col xs={12} md={6}>
                <Typography type="subheading">Conta: {accountName}</Typography>
              </Col>
              <Col xs={12} md={6}>
                <Typography type="subheading">
                  Entidade: {entityName}
                </Typography>
              </Col>
            </Row>

            <Divider className="mv-sm" />

            <Row className="mb-0">
              <Col xs={12}>
                <Typography gutterBottom noWrap>
                  Marque os pagamentos que deseja associar a uma conta de
                  recebível e seleciona a regra no campo abaixo.
                </Typography>
              </Col>
            </Row>
            <Row className="mt-0 mb-sm">
              <Col xs={12}>
                {this.allAccountsSelectField(
                  payments.filter(payment => !!payment.accounts_found)
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <DataTable
                  manual
                  resultSet={payments}
                  config={this.config}
                  perPage={perPage}
                  page={page}
                  total={total}
                  onPageChange={page => {
                    presenter.onPageChange(page + 1);
                    this.clearCheckBoxes();
                  }}
                />
              </Col>
            </Row>
          </DialogContent>

          <DialogActions>
            <StyledButton
              flat
              accent
              label="Cancelar"
              onTouchTap={() => onPendingTransactionsClose()}
              style={{ margin: "4px 0px 0px 0px", color: colors.accent.normal }}
            />
            <StyledButton
              primary
              label="Confirmar"
              onTouchTap={() => presenter.onSendTable()}
            />
          </DialogActions>
        </Loadable>
      </Dialog>
    );
  }
}

export default style(MyFinancePendingTransactionsView);



// WEBPACK FOOTER //
// ./src/components/app/registration/company/form/integrations/my-finance/pending-transactions/view.jsx